import { LoadingSpinner } from "components/LoadingSpinner";
import { orderTypeConstants } from "constants/order";
import { distributionLogicValueList } from "constants/paymentPlanConstants";
import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import dayjs from "dayjs";
import { parseDate, parseTime } from "helpers/date";
import { centsToDollars } from "helpers/price";
import useDocumentTitle from "hooks/useDocumentTitle";
import { usePayerInformation } from "hooks/usePayerInformation";
import { CLEAR_HEALTH_PAYER_NAME } from "hooks/useProviderProcedure";
import { isEmpty } from "lodash";
import { useMemo, useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useGetOrderQuery } from "store/queries/order";
import { useSearchProviderProcedureMutation } from "store/queries/providerProcedure";
import { onSelectOrderToCopy } from "store/slices/order";
import {
  setAutoCompleteValue,
  setRows,
  setSelectedProcedures,
} from "store/slices/serviceDetails";
import {
  IOrder,
  IGFEServiceDetails,
  IDefaultServiceDetails,
  IPatientResponsibilityServiceDetails,
} from "types/Order/Order";

import {
  Header,
  OrderType,
  PatientInfoForm,
  ServiceDetails,
  VisitForm,
  Submit,
  Communication,
  PatientBenefitsSection,
} from "./components";
import { PaymentPlan } from "./components/PaymentPlan/PaymentPlan";
import {
  buildSelectedProceduresArray,
  buildSelectedProceduresObject,
  buildSelectedProceduresToTable,
} from "./components/ServiceDetails/builders";
import { formatPhoneNumberToMask } from "./utils/mask";

function CopyOrderForm({ orderToCopy }: { orderToCopy: IOrder }) {
  const dispatch = useDispatch();
  const [searchMutation] = useSearchProviderProcedureMutation();
  const [currentProviderProcedures, setCurrentProviderProcedures] =
    useState<any>([]);
  const {
    serviceDetails,
    patient,
    procedures,
    providerProcedures,
    amountInCents,
    accountNumber,
    dateOfService,
    orderType,
    accountId,
    gfeQuote,
    paymentPlan,
    account,
  } = orderToCopy;

  const { payerNameOptions } = usePayerInformation(account.id);

  const estimatedVisitCost = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return centsToDollars(
        (serviceDetails as IGFEServiceDetails)
          .patientResponsibilityEstimateAmountInCents || 0
      );
    }
    return centsToDollars(
      (serviceDetails as IDefaultServiceDetails).estimatedVisitCost || 0
    );
  }, [orderType, serviceDetails]);

  const payerName = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return payerNameOptions.find(
        (aOption: { name: string; value: string }) =>
          aOption.name.toLowerCase() === orderToCopy?.payer?.name?.toLowerCase()
      );
    }
    return null;
  }, [orderType, payerNameOptions, orderToCopy?.payer?.name]);

  const payerName2 = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return payerNameOptions.find(
        (aOption: { name: string; value: string }) =>
          aOption?.name?.toLowerCase() ===
          orderToCopy?.secondaryPayer?.name?.toLowerCase()
      );
    }
    return null;
  }, [orderType, payerNameOptions, orderToCopy?.secondaryPayer?.name]);

  const paymentPlanActive = useMemo(
    () => paymentPlan.find((paymentPlan) => paymentPlan.active),
    [paymentPlan]
  );

  const selectedProceduresList = useMemo(() => {
    return serviceDetails.procedures.map(
      (procedure) => `${procedure.code} - ${procedure.cptDescription}`
    );
  }, [serviceDetails]);

  const initializeDefault = async () => {
    const autoCompleteValue = [
      ...new Set(
        serviceDetails.procedures.map(
          (procedure) =>
            `${procedure.code} - ${
              procedures?.find((p) => p.cptCode === procedure.code.toString())
                ?.description
            }`
        )
      ),
    ];
    const selectedProcedureArrayWithDefaultDescriptions =
      buildSelectedProceduresArray({
        procedures: (serviceDetails as IGFEServiceDetails).procedures.map(
          (procedure) => {
            const defaultDescription = procedures?.find(
              (p) => p.cptCode === procedure.code.toString()
            )?.description;
            return {
              code: procedure.code,
              providerProcedureId: procedure.providerProcedureId,
              specialty: procedure.specialty,
              cptDescription: defaultDescription || "",
            };
          }
        ),
      });
    const selectedProcedureArray = buildSelectedProceduresArray({
      procedures: (serviceDetails as IGFEServiceDetails).procedures.map(
        (procedure) => {
          return {
            code: procedure.code,
            providerProcedureId: procedure.providerProcedureId,
            specialty: procedure.specialty,
            cptDescription: procedure.cptDescription,
          };
        }
      ),
    });
    const selectedProceduresObject = buildSelectedProceduresObject(
      selectedProcedureArray
    );
    const rowsToSet = buildSelectedProceduresToTable({
      selectedProceduresObject,
      procedures: procedures || [],
      providerProcedures: providerProcedures || [],
      rows: [],
      // Don't pass originalProcedures here - we'll match after fetching all providers
    });
    const rowsWithoutDuplicateProviders = rowsToSet.map((row: any) => {
      const uniqueProviders = row.providerDropdown.filter(
        (provider: any, index: any, self: any) =>
          index === self.findIndex((p: any) => p.id === provider.id)
      );
      return {
        ...row,
        providerDropdown: uniqueProviders,
      };
    });

    // We'll set the correct amount after matching providers in rowsWithAllAvailableProviders
    const rowsWithCurrentAmount = rowsWithoutDuplicateProviders;

    const fetchedProviderProcedures: any[] = await Promise.all(
      selectedProceduresObject.map(async (procedure) => {
        const result = (await searchMutation({
          accountId,
          cptCode: procedure.code,
          payerName:
            orderType === orderTypeConstants.GFE
              ? serviceDetails.payerName
              : CLEAR_HEALTH_PAYER_NAME,
        })) as any;
        return result.data.data.data;
      })
    ).then((results) => results.flat());

    const rowsWithAllAvailableProviders = rowsWithCurrentAmount.map((row) => {
      const providerData = [...row.providerDropdown];
      fetchedProviderProcedures.forEach((providerProcedure) => {
        if (
          row.cptCode === providerProcedure.procedure.cptCode &&
          row.specialty === providerProcedure.provider.specialty &&
          row.procedureId === providerProcedure.procedureId &&
          !providerData.find((p) => p.id === providerProcedure.provider.id)
        ) {
          providerData.push({
            id: providerProcedure.provider.id,
            name: providerProcedure.provider.name,
            specialty: providerProcedure.provider.specialty,
            priceInCents: providerProcedure.priceInCents,
          });
        }
      });

      // Now match the provider from the original order
      const originalProcedure = (
        serviceDetails as IGFEServiceDetails
      ).procedures.find(
        (p) => p.code === row.cptCode && p.specialty === row.specialty
      );

      let { selectedProvider } = row;
      let selectedAmount = row.amount;
      let selectedProviderProcedureId = row.providerProcedureId;

      if (originalProcedure?.providerId) {
        const matchingProvider = providerData.find(
          (p) => p.id === originalProcedure.providerId?.toString()
        );

        if (matchingProvider) {
          selectedProvider = matchingProvider;
          // Use the custom amount from the original order if it exists, otherwise use provider's default price
          selectedAmount =
            originalProcedure.amountInCents !== undefined &&
            originalProcedure.amountInCents !== null
              ? originalProcedure.amountInCents
              : matchingProvider.priceInCents;

          // Find the matching providerProcedure to get the correct providerProcedureId
          const matchingProviderProcedure = fetchedProviderProcedures.find(
            (pp: any) =>
              pp.providerId === originalProcedure.providerId?.toString() &&
              pp.provider.specialty === row.specialty &&
              pp.procedure.cptCode === row.cptCode
          );
          if (matchingProviderProcedure) {
            selectedProviderProcedureId = matchingProviderProcedure.id;
          }
        }
      }

      return {
        ...row,
        providerDropdown: providerData,
        selectedProvider,
        amount: selectedAmount,
        providerProcedureId: selectedProviderProcedureId,
      };
    });

    const rowsWithBenefitSelectionType = rowsWithAllAvailableProviders.map(
      (row) => {
        const procedure = serviceDetails.procedures.find(
          (p) =>
            p.code === row.cptCode &&
            p.specialty === row.specialty &&
            p.providerId === row.selectedProvider.id &&
            p.providerProcedureId === row.providerProcedureId
        );
        return {
          ...row,
          benefitSelectionType:
            procedure?.benefitsForProvProc?.benefitSelectionType,
        };
      }
    );

    dispatch(setAutoCompleteValue(autoCompleteValue));
    dispatch(
      setSelectedProcedures(selectedProcedureArrayWithDefaultDescriptions)
    );
    dispatch(setRows(rowsWithBenefitSelectionType));
    setCurrentProviderProcedures(fetchedProviderProcedures);
  };

  useEffect(() => {
    if (orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY) {
      initializeDefault();
    }
  }, []);

  const methods = useForm({
    defaultValues: {
      email: patient.email,
      amount: centsToDollars(
        (serviceDetails as IGFEServiceDetails)
          .patientResponsibilityEstimateAmountInCents || 0
      ),
      lastName: patient.lastName,
      firstName: patient.firstName,
      dateOfBirth: parseDate(patient.dateOfBirth),
      totalAmount: centsToDollars(amountInCents || 0),
      phoneNumber: patient.phoneNumber
        ? formatPhoneNumberToMask(patient.phoneNumber)
        : null,
      accountNumber,
      dateOfService: parseDate(dateOfService),
      timeOfService: parseTime(dateOfService),
      serviceDetails: serviceDetails.procedures,
      preferredContactMethod: patient.preferredContactMethod,
      estimatedVisitCostForPatient: estimatedVisitCost,
      orderType,
      accountId,
      payerName: payerName?.name || null,
      payerName2: payerName2?.name || null,
      payer: null,
      payer2: null,
      placeOfService: null,
      locality: null,
      deductible: centsToDollars(gfeQuote?.remainingDeductibleInCents || 0),
      outOfPocketMax: centsToDollars(gfeQuote?.outOfPocketMaxInCents || 0),
      flatCopay: centsToDollars(gfeQuote?.flatCopayInCents || 0),
      professionalFlatCopay:
        orderToCopy?.benefitDetails?.response?.professionalCopay ||
        centsToDollars(gfeQuote?.professionalFlatCopayInCents || 0),
      showProfessionalCopay:
        (orderToCopy?.benefitDetails?.response?.professionalCopay || 0) > 0 ||
        (gfeQuote?.professionalFlatCopayInCents || 0) > 0,
      coinsurancePercent: gfeQuote?.coinsurancePercent || 0,
      deductible2: centsToDollars(
        gfeQuote?.secondaryPatientBenefits?.deductible || 0
      ),
      outOfPocketMax2: centsToDollars(
        gfeQuote?.secondaryPatientBenefits?.outOfPocketMax || 0
      ),
      flatCopay2: centsToDollars(
        gfeQuote?.secondaryPatientBenefits?.flatCopay || 0
      ),
      professionalFlatCopay2:
        orderToCopy?.secondaryBenefitDetails?.response?.professionalCopay ||
        centsToDollars(
          gfeQuote?.secondaryPatientBenefits?.professionalFlatCopay || 0
        ),
      showProfessionalCopay2:
        (orderToCopy?.secondaryBenefitDetails?.response?.professionalCopay ||
          0) > 0 ||
        (gfeQuote?.secondaryPatientBenefits?.professionalFlatCopay || 0) > 0,
      coinsurancePercent2:
        gfeQuote?.secondaryPatientBenefits?.coinsurancePercent || 0,
      gfeQuoteId: null,
      practiceType:
        orderToCopy?.benefitDetails?.request?.practiceTypeCode ||
        mapPayerNameToCodeForDropdown[0]?.value,
      practiceType2:
        orderToCopy?.secondaryBenefitDetails?.request?.practiceTypeCode ||
        mapPayerNameToCodeForDropdown[0]?.value,
      outOfPocketReason: serviceDetails?.outOfPocketReason
        ? { id: "", name: serviceDetails?.outOfPocketReason }
        : null,
      orderId: null,
      subscriberFirstName: patient.firstName,
      subscriberLastName: patient.lastName,
      subscriberDOB: parseDate(patient.dateOfBirth),
      memberId: patient?.patientBenefits?.memberID || null,
      memberId2: patient?.secondaryPatientBenefits?.memberID || null,
      isSubscriberPatient:
        patient?.patientBenefits?.isSubscriberPatient || null,
      paymentPlanDuration: null,
      paymentPlanMinDownPaymentType: null,
      paymentPlanMinDownPaymentFlat: null,
      paymentPlanMinDownPaymentPercent: null,
      paymentPlanEnabled: true,
      copyOrderFirstRender: true,
      outreachEnabled: true,
      providerDistributionLogic:
        paymentPlanActive?.providerDistributionLogic ||
        account.providerDistributionLogic ||
        distributionLogicValueList.BY_PRIORITY,
      facilityCopay: !!gfeQuote?.costApplicationRules?.facilityRules?.useCopay,
      facilityDeductible:
        !!gfeQuote?.costApplicationRules?.facilityRules?.useDeductible,
      facilityCoinsurance:
        !!gfeQuote?.costApplicationRules?.facilityRules?.useCoinsurance,
      professionalCopay:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useCopay,
      professionalDeductible:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useDeductible,
      professionalCoinsurance:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useCoinsurance,
      secondaryInsuranceActivated: !!orderToCopy?.secondaryPayerId,
      facilityCopay2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useCopay,
      facilityDeductible2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useDeductible,
      facilityCoinsurance2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useCoinsurance,
      professionalCopay2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useCopay,
      professionalDeductible2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useDeductible,
      professionalCoinsurance2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useCoinsurance,
      benefitDetails: orderToCopy.benefitDetails || null,
      secondaryBenefitDetails: orderToCopy.secondaryBenefitDetails || null,
      pverifyResponseId: orderToCopy.pverifyResponse?.id || null,
      benefitsUpdatedAt: gfeQuote?.benefitsUpdatedAt,
      secondaryPverifyResponseId:
        orderToCopy.secondaryPverifyResponse?.id || null,
      secondaryBenefitsUpdatedAt:
        gfeQuote?.secondaryPatientBenefits?.benefitsUpdatedAt,
      ...(orderType === orderTypeConstants.PATIENT_RESPONSIBILITY && {
        totalInsuranceAllowable:
          (serviceDetails as IPatientResponsibilityServiceDetails)
            ?.totalInsuranceAllowableInCents === null
            ? null
            : centsToDollars(
                (serviceDetails as IPatientResponsibilityServiceDetails)
                  ?.totalInsuranceAllowableInCents || 0
              ),
      }),
      scheduledFirstCommunicationDate: null,
      scheduledFirstCommunicationSent: false,
      sendDefaultText: true,
    },
  });
  return (
    <FormProvider {...methods}>
      <Header />
      {account && <OrderType orderTypesVisible={account.orderTypesVisible} />}
      <PatientInfoForm />
      <VisitForm skipDuplicateCheck />
      <PatientBenefitsSection
        copy
        account={account}
        costApplicationRules={gfeQuote?.costApplicationRules}
      />
      <ServiceDetails
        copy
        selectedProceduresList={selectedProceduresList}
        currentProviderProcedures={currentProviderProcedures}
      />
      {!!account && <PaymentPlan accountData={{ data: account }} />}

      {account && <Communication account={account} />}
      <Submit />
    </FormProvider>
  );
}

export function CopyOrder() {
  useDocumentTitle("Copy Order");
  const { id } = useParams();
  const dispatch = useDispatch();
  const { data: order, isLoading } = useGetOrderQuery(id);
  const orderToCopy = useMemo(() => {
    return order?.data as IOrder;
  }, [order]);

  useEffect(() => {
    if (!isEmpty(orderToCopy)) {
      dispatch(onSelectOrderToCopy(orderToCopy as IOrder));
    }
  }, [orderToCopy]);

  if (isLoading || !orderToCopy) {
    return <LoadingSpinner />;
  }
  return <CopyOrderForm orderToCopy={orderToCopy} />;
}
